import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Select from "react-select";
import { classList, sectionList } from "../../utils";

/**
 * AttendanceReports
 *
 * Mirrors the StudentReports layout:
 *  - Left col  : Hero header + dataset selector + filters + live snapshot
 *  - Right col : Report panel (category tabs + report buttons + quick actions)
 *
 * All API calls use the same patterns found in the existing attendance pages.
 * Replace `server` with your actual import.
 */

const server = ""; // import { server } from "../../server";

// ─── tiny helpers ────────────────────────────────────────────────────────────
const toUpper = (v) =>
  v !== null && v !== undefined ? String(v).toUpperCase() : "";

const today = new Date().toISOString().split("T")[0];

const writeWorkbook = (workbook, filename) => {
  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  saveAs(blob, filename);
  toast.success("Report Generated");
};

const sortByRoll = (arr) =>
  arr.sort((a, b) => Number(a["ROLL NO"] || 0) - Number(b["ROLL NO"] || 0));

// ─── dataset definitions ──────────────────────────────────────────────────────
const DATASETS = {
  school: { label: "School Attendance", color: "success" },
  bus: { label: "Bus Attendance", color: "warning" },
  classsection: { label: "Class / Section", color: "info" },
  leave: { label: "Applied Leave", color: "danger" },
};

// ─── report categories ────────────────────────────────────────────────────────
const REPORT_CATEGORIES = [
  { key: "all", label: "All Reports" },
  { key: "school", label: "School" },
  { key: "bus", label: "Bus" },
  { key: "leave", label: "Leave" },
  { key: "analytics", label: "Analytics" },
  { key: "compliance", label: "Compliance" },
];

function AttendanceReports() {
  // ── state ──────────────────────────────────────────────────────────────────
  const [activeDataset, setActiveDataset] = useState("school");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  // raw data per dataset
  const [schoolData, setSchoolData] = useState([]);
  const [busData, setBusData] = useState([]);
  const [classSectionData, setClassSectionData] = useState([]);
  const [leaveData, setLeaveData] = useState([]);

  const [loadingSchool, setLoadingSchool] = useState(false);
  const [loadingBus, setLoadingBus] = useState(false);
  const [loadingClassSection, setLoadingClassSection] = useState(false);
  const [loadingLeave, setLoadingLeave] = useState(false);

  // ── fetch helpers ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAll = async () => {
      // School attendance
      setLoadingSchool(true);
      try {
        const res = await fetch(
          `${server}/student_attendance_of_school_bydate_mongodb`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: localStorage.getItem("token"),
            },
            body: JSON.stringify({ date: today }),
          },
        );
        const json = await res.json();
        const parsed = JSON.parse(json?.["body-json"]?.body || "{}");
        setSchoolData(parsed.schoolAttendance || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingSchool(false);
      }

      // Bus attendance
      setLoadingBus(true);
      try {
        const res = await fetch(
          `${server}/school_attendance_of_buspickup_and_busdrop_mongodb`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: localStorage.getItem("token"),
            },
            body: JSON.stringify({ date: today }),
          },
        );
        const json = await res.json();
        setBusData(json?.["body-json"]?.body?.attendance || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingBus(false);
      }

      // Applied leave
      setLoadingLeave(true);
      try {
        const res = await fetch(`${server}/getAppliedLeave_mongodb`, {
          headers: { Authorization: localStorage.getItem("token") },
        });
        const json = await res.json();
        setLeaveData(json?.["body-json"]?.body?.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingLeave(false);
      }
    };

    fetchAll();
  }, []);

  // fetch class/section attendance when class or section changes
  useEffect(() => {
    if (!selectedClass) return;
    const sec = selectedSection || "A";
    setLoadingClassSection(true);
    fetch(`${server}/student_attendence_by_classandsection`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("token"),
      },
      body: JSON.stringify({ class: selectedClass, section: sec }),
    })
      .then((r) => r.json())
      .then((json) => {
        setClassSectionData(json?.["body-json"]?.body?.students || []);
      })
      .catch(console.error)
      .finally(() => setLoadingClassSection(false));
  }, [selectedClass, selectedSection]);

  // ── current data ───────────────────────────────────────────────────────────
  const datasetMap = {
    school: { data: schoolData, loading: loadingSchool },
    bus: { data: busData, loading: loadingBus },
    classsection: { data: classSectionData, loading: loadingClassSection },
    leave: { data: leaveData, loading: loadingLeave },
  };

  const currentRaw = datasetMap[activeDataset]?.data || [];
  const currentLoading = datasetMap[activeDataset]?.loading;

  // ── filtered data ──────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let data = [...currentRaw];

    if (selectedClass && activeDataset === "classsection") {
      data = data.filter((s) => s.class === selectedClass);
    }
    if (selectedSection && activeDataset === "classsection") {
      data = data.filter((s) => s.section === selectedSection);
    }

    if (fromDate || toDate) {
      data = data.filter((s) => {
        const d = s.date || s.createdAt || s.fromDate;
        if (!d) return false;
        const ds = d.split("T")[0];
        if (fromDate && ds < fromDate) return false;
        if (toDate && ds > toDate) return false;
        return true;
      });
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      const flatten = (obj) => {
        const vals = [];
        for (const k in obj) {
          const v = obj[k];
          if (v && typeof v === "object") vals.push(...flatten(v));
          else if (typeof v === "string") vals.push(v);
        }
        return vals;
      };
      data = data.filter((s) =>
        flatten(s).some((v) => v.toLowerCase().includes(lower)),
      );
    }

    return data;
  }, [
    currentRaw,
    selectedClass,
    selectedSection,
    fromDate,
    toDate,
    searchTerm,
    activeDataset,
  ]);

  // ── live snapshot summary ──────────────────────────────────────────────────
  const summary = useMemo(() => {
    const total = filteredData.length;
    let present = 0,
      absent = 0,
      busPickupPresent = 0,
      busDropPresent = 0,
      pendingLeave = 0,
      approvedLeave = 0;

    filteredData.forEach((s) => {
      // school
      if (s?.school?.status === "Present") present++;
      if (s?.school?.status === "Absent") absent++;
      // bus
      if (s?.busPickup?.status === "Present") busPickupPresent++;
      if (s?.busDrop?.status === "Present") busDropPresent++;
      // leave
      if (s?.status?.toLowerCase() === "pending") pendingLeave++;
      if (s?.status?.toLowerCase() === "approved") approvedLeave++;
    });

    return {
      total,
      present,
      absent,
      busPickupPresent,
      busDropPresent,
      pendingLeave,
      approvedLeave,
    };
  }, [filteredData]);

  // ── class/section options for selects ─────────────────────────────────────
  const classOptions = classList.map((c) => ({ value: c, label: c }));
  const sectionOptions = sectionList.map((s) => ({ value: s, label: s }));

  // ── select styles (same as StudentReports) ─────────────────────────────────
  const selectStyles = {
    control: (base) => ({
      ...base,
      border: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      minHeight: "32px",
      cursor: "pointer",
    }),
    valueContainer: (base) => ({ ...base, padding: "0" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      border: "none",
      zIndex: 5,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#ffc107"
        : state.isFocused
          ? "#f8f9fa"
          : "white",
      color: state.isSelected ? "white" : "#212529",
      cursor: "pointer",
      fontSize: "14px",
    }),
    placeholder: (base) => ({ ...base, color: "#6c757d", fontSize: "14px" }),
    singleValue: (base) => ({ ...base, color: "#212529", fontSize: "14px" }),
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT GENERATORS
  // ─────────────────────────────────────────────────────────────────────────

  // ── helpers ────────────────────────────────────────────────────────────────
  const flattenAttendanceRow = (rec) => ({
    "ADMISSION NO": toUpper(rec.admission_no || rec.admissionNo || ""),
    "STUDENT NAME": toUpper(rec.name || ""),
    CLASS: toUpper(rec.class || ""),
    SECTION: toUpper(rec.section || ""),
    "ROLL NO": toUpper(rec.rollNo || ""),
    "PHONE NO": toUpper(rec.phoneNo || rec.phone || ""),
    "BUS NO": toUpper(rec.busNo || rec.busNumber || ""),
    DATE: toUpper(rec.date || rec.createdAt || ""),
    "SCHOOL STATUS": toUpper(rec?.school?.status || ""),
    "SCHOOL CREATED BY": toUpper(rec?.school?.createdBy || ""),
    "BUS PICKUP STATUS": toUpper(rec?.busPickup?.status || ""),
    "BUS DROP STATUS": toUpper(rec?.busDrop?.status || ""),
  });

  const flattenLeaveRow = (rec) => ({
    "LEAVE ID": toUpper(rec.leaveId || ""),
    "ADMISSION NO": toUpper(rec.admission_no || ""),
    "STUDENT NAME": toUpper(rec.name || ""),
    CLASS: toUpper(rec.class || ""),
    SECTION: toUpper(rec.section || ""),
    "LEAVE FROM": toUpper(rec.fromDate || ""),
    "LEAVE TO": toUpper(rec.toDate || ""),
    REASON: toUpper(rec.reason || ""),
    STATUS: toUpper(rec.status || ""),
    "APPLIED DATE": toUpper(rec.createdAt || ""),
    DOCUMENT: toUpper(rec.document || ""),
  });

  // ── school attendance reports ──────────────────────────────────────────────
  const reportSchoolAll = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = filteredData.map(flattenAttendanceRow);
    sortByRoll(rows);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "SCHOOL ATTENDANCE",
    );
    writeWorkbook(wb, `SchoolAttendance_${today}.xlsx`);
  };

  const reportSchoolPresent = () => {
    const list = filteredData.filter((s) => s?.school?.status === "Present");
    if (!list.length) {
      toast.error("No present records found");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = list.map(flattenAttendanceRow);
    sortByRoll(rows);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "PRESENT");
    writeWorkbook(wb, `SchoolAttendance_Present_${today}.xlsx`);
  };

  const reportSchoolAbsent = () => {
    const list = filteredData.filter((s) => s?.school?.status === "Absent");
    if (!list.length) {
      toast.error("No absent records found");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = list.map(flattenAttendanceRow);
    sortByRoll(rows);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "ABSENT");
    writeWorkbook(wb, `SchoolAttendance_Absent_${today}.xlsx`);
  };

  const reportSchoolClassWise = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    filteredData.forEach((s) => {
      const cls = s.class || "UNKNOWN";
      if (!groups[cls]) groups[cls] = [];
      groups[cls].push(flattenAttendanceRow(s));
    });
    Object.entries(groups).forEach(([cls, rows]) => {
      sortByRoll(rows);
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(cls).slice(0, 31),
      );
    });
    writeWorkbook(wb, `SchoolAttendance_ClassWise_${today}.xlsx`);
  };

  const reportSchoolSectionWise = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    filteredData.forEach((s) => {
      const key = `${s.class || "?"}-${s.section || "?"}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(flattenAttendanceRow(s));
    });
    Object.entries(groups).forEach(([key, rows]) => {
      sortByRoll(rows);
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(key).slice(0, 31),
      );
    });
    writeWorkbook(wb, `SchoolAttendance_SectionWise_${today}.xlsx`);
  };

  const reportSchoolDateRange = () => {
    if (!fromDate || !toDate) {
      toast.error("Please select From and To dates");
      return;
    }
    if (!filteredData.length) {
      toast.error("No records in this date range");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = filteredData
      .map(flattenAttendanceRow)
      .sort((a, b) => (a["DATE"] || "").localeCompare(b["DATE"] || ""));
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "DATE RANGE",
    );
    writeWorkbook(wb, `SchoolAttendance_${fromDate}_to_${toDate}.xlsx`);
  };

  // ── bus attendance reports ─────────────────────────────────────────────────
  const reportBusAll = () => {
    if (!busData.length) {
      toast.error("No bus records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = busData.map(flattenAttendanceRow);
    sortByRoll(rows);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "BUS ATTENDANCE",
    );
    writeWorkbook(wb, `BusAttendance_${today}.xlsx`);
  };

  const reportBusPickupPresent = () => {
    const list = busData.filter((s) => s?.busPickup?.status === "Present");
    if (!list.length) {
      toast.error("No pickup-present records");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = list.map(flattenAttendanceRow);
    sortByRoll(rows);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "PICKUP PRESENT",
    );
    writeWorkbook(wb, `BusPickup_Present_${today}.xlsx`);
  };

  const reportBusPickupAbsent = () => {
    const list = busData.filter((s) => s?.busPickup?.status === "Absent");
    if (!list.length) {
      toast.error("No pickup-absent records");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = list.map(flattenAttendanceRow);
    sortByRoll(rows);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "PICKUP ABSENT",
    );
    writeWorkbook(wb, `BusPickup_Absent_${today}.xlsx`);
  };

  const reportBusDropPresent = () => {
    const list = busData.filter((s) => s?.busDrop?.status === "Present");
    if (!list.length) {
      toast.error("No drop-present records");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = list.map(flattenAttendanceRow);
    sortByRoll(rows);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "DROP PRESENT",
    );
    writeWorkbook(wb, `BusDrop_Present_${today}.xlsx`);
  };

  const reportBusWise = () => {
    if (!busData.length) {
      toast.error("No bus records");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    busData.forEach((s) => {
      const bus = s.busNo || s.busNumber || "UNKNOWN";
      if (!groups[bus]) groups[bus] = [];
      groups[bus].push(flattenAttendanceRow(s));
    });
    Object.entries(groups).forEach(([bus, rows]) => {
      sortByRoll(rows);
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        `BUS ${toUpper(bus)}`.slice(0, 31),
      );
    });
    writeWorkbook(wb, `BusWise_Attendance_${today}.xlsx`);
  };

  // ── leave reports ──────────────────────────────────────────────────────────
  const leaveByStatus = (status, filename) => {
    const list = leaveData.filter(
      (s) => (s.status || "").toLowerCase() === status,
    );
    if (!list.length) {
      toast.error(`No ${status} leave records`);
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = list.map(flattenLeaveRow);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      toUpper(status),
    );
    writeWorkbook(wb, `${filename}_${today}.xlsx`);
  };

  const reportLeaveAll = () => {
    if (!leaveData.length) {
      toast.error("No leave records");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = leaveData.map(flattenLeaveRow);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "ALL LEAVES",
    );
    writeWorkbook(wb, `AppliedLeave_All_${today}.xlsx`);
  };

  const reportLeavePending = () =>
    leaveByStatus("pending", "AppliedLeave_Pending");
  const reportLeaveApproved = () =>
    leaveByStatus("approved", "AppliedLeave_Approved");
  const reportLeaveRejected = () =>
    leaveByStatus("rejected", "AppliedLeave_Rejected");

  const reportLeaveClassWise = () => {
    if (!leaveData.length) {
      toast.error("No leave records");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    leaveData.forEach((s) => {
      const cls = s.class || "UNKNOWN";
      if (!groups[cls]) groups[cls] = [];
      groups[cls].push(flattenLeaveRow(s));
    });
    Object.entries(groups).forEach(([cls, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(cls).slice(0, 31),
      );
    });
    writeWorkbook(wb, `AppliedLeave_ClassWise_${today}.xlsx`);
  };

  const reportLeaveWithDocument = () => {
    const list = leaveData.filter(
      (s) => s.document && s.document.trim() !== "",
    );
    if (!list.length) {
      toast.error("No leave records with documents");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = list.map(flattenLeaveRow);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "WITH DOCUMENT",
    );
    writeWorkbook(wb, `AppliedLeave_WithDocument_${today}.xlsx`);
  };

  // ── analytics / compliance ────────────────────────────────────────────────
  const reportAttendanceSummary = () => {
    // class-wise present / absent summary from school data
    if (!schoolData.length) {
      toast.error("No school attendance data");
      return;
    }
    const groups = {};
    schoolData.forEach((s) => {
      const cls = s.class || "UNKNOWN";
      const sec = s.section || "-";
      const key = `${cls}||${sec}`;
      if (!groups[key])
        groups[key] = {
          Class: cls,
          Section: sec,
          Total: 0,
          Present: 0,
          Absent: 0,
        };
      groups[key].Total++;
      if (s?.school?.status === "Present") groups[key].Present++;
      if (s?.school?.status === "Absent") groups[key].Absent++;
    });
    const rows = Object.values(groups);
    const totals = rows.reduce(
      (acc, r) => {
        acc.Total += r.Total;
        acc.Present += r.Present;
        acc.Absent += r.Absent;
        return acc;
      },
      { Class: "TOTAL", Section: "", Total: 0, Present: 0, Absent: 0 },
    );
    rows.push(totals);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Summary");
    writeWorkbook(wb, `AttendanceSummary_${today}.xlsx`);
  };

  const reportBusSummary = () => {
    if (!busData.length) {
      toast.error("No bus attendance data");
      return;
    }
    const groups = {};
    busData.forEach((s) => {
      const bus = String(s.busNo || s.busNumber || "UNKNOWN");
      if (!groups[bus])
        groups[bus] = {
          "Bus No": bus,
          "Pickup Present": 0,
          "Pickup Absent": 0,
          "Drop Present": 0,
          "Drop Absent": 0,
        };
      if (s?.busPickup?.status === "Present") groups[bus]["Pickup Present"]++;
      if (s?.busPickup?.status === "Absent") groups[bus]["Pickup Absent"]++;
      if (s?.busDrop?.status === "Present") groups[bus]["Drop Present"]++;
      if (s?.busDrop?.status === "Absent") groups[bus]["Drop Absent"]++;
    });
    const rows = Object.values(groups).sort(
      (a, b) => Number(a["Bus No"]) - Number(b["Bus No"]),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "Bus Summary",
    );
    writeWorkbook(wb, `BusAttendanceSummary_${today}.xlsx`);
  };

  const reportLeaveSummary = () => {
    if (!leaveData.length) {
      toast.error("No leave data");
      return;
    }
    const groups = {};
    leaveData.forEach((s) => {
      const cls = s.class || "UNKNOWN";
      if (!groups[cls])
        groups[cls] = {
          Class: cls,
          Total: 0,
          Pending: 0,
          Approved: 0,
          Rejected: 0,
        };
      groups[cls].Total++;
      const st = (s.status || "").toLowerCase();
      if (st === "pending") groups[cls].Pending++;
      if (st === "approved") groups[cls].Approved++;
      if (st === "rejected") groups[cls].Rejected++;
    });
    const rows = Object.values(groups);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "Leave Summary",
    );
    writeWorkbook(wb, `LeaveSummary_${today}.xlsx`);
  };

  const reportMasterCombined = () => {
    const master = [];
    schoolData.forEach((s) => {
      const row = flattenAttendanceRow(s);
      row["TYPE"] = "SCHOOL";
      master.push(row);
    });
    busData.forEach((s) => {
      const row = flattenAttendanceRow(s);
      row["TYPE"] = "BUS";
      master.push(row);
    });
    if (!master.length) {
      toast.error("No data available");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(master),
      "MASTER",
    );
    writeWorkbook(wb, `AttendanceMasterReport_${today}.xlsx`);
  };

  const reportNotMarkedClasses = () => {
    // classes where no student has school status marked
    if (!schoolData.length) {
      toast.error("No data");
      return;
    }
    const classMarked = {};
    schoolData.forEach((s) => {
      const cls = `${s.class || "?"}${s.section || ""}`;
      if (!classMarked[cls]) classMarked[cls] = false;
      if (s?.school?.status) classMarked[cls] = true;
    });
    const notMarked = Object.entries(classMarked)
      .filter(([, v]) => !v)
      .map(([cls]) => ({ "Class/Section": cls, Status: "NOT MARKED" }));
    if (!notMarked.length) {
      toast.error("All classes have attendance marked");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(notMarked),
      "NOT MARKED",
    );
    writeWorkbook(wb, `NotMarkedClasses_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT LIST
  // ─────────────────────────────────────────────────────────────────────────
  const reports = [
    // School
    { cat: "school", label: "All School Attendance", action: reportSchoolAll },
    { cat: "school", label: "Present Students", action: reportSchoolPresent },
    { cat: "school", label: "Absent Students", action: reportSchoolAbsent },
    { cat: "school", label: "Class Wise", action: reportSchoolClassWise },
    {
      cat: "school",
      label: "Class / Section Wise",
      action: reportSchoolSectionWise,
    },
    { cat: "school", label: "Date Range", action: reportSchoolDateRange },
    // Bus
    { cat: "bus", label: "All Bus Attendance", action: reportBusAll },
    { cat: "bus", label: "Pickup — Present", action: reportBusPickupPresent },
    { cat: "bus", label: "Pickup — Absent", action: reportBusPickupAbsent },
    { cat: "bus", label: "Drop — Present", action: reportBusDropPresent },
    { cat: "bus", label: "Bus No Wise", action: reportBusWise },
    // Leave
    { cat: "leave", label: "All Applied Leaves", action: reportLeaveAll },
    { cat: "leave", label: "Pending", action: reportLeavePending },
    { cat: "leave", label: "Approved", action: reportLeaveApproved },
    { cat: "leave", label: "Rejected", action: reportLeaveRejected },
    { cat: "leave", label: "Class Wise", action: reportLeaveClassWise },
    { cat: "leave", label: "Having Document", action: reportLeaveWithDocument },
    // Analytics
    {
      cat: "analytics",
      label: "Attendance Summary",
      action: reportAttendanceSummary,
    },
    {
      cat: "analytics",
      label: "Bus Attendance Summary",
      action: reportBusSummary,
    },
    { cat: "analytics", label: "Leave Summary", action: reportLeaveSummary },
    {
      cat: "analytics",
      label: "Master Combined Report",
      action: reportMasterCombined,
    },
    // Compliance
    {
      cat: "compliance",
      label: "Not Marked Classes",
      action: reportNotMarkedClasses,
    },
  ];

  const visibleReports =
    activeCategory === "all"
      ? reports
      : reports.filter((r) => r.cat === activeCategory);

  // ── reset filters ──────────────────────────────────────────────────────────
  const resetFilters = () => {
    setSelectedClass("");
    setSelectedSection("");
    setFromDate("");
    setToDate("");
    setSearchTerm("");
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                {/* HERO HEADER */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="bg-light rounded-4 p-4 position-relative overflow-hidden">
                    <div className="d-flex align-items-center">
                      <p className="badge bg-white text-dark rounded-pill px-4 py-2 mb-0 fs-6 fw-medium">
                        <i className="bi bi-calendar-check me-1"></i> Reports &
                        Analytics
                      </p>
                      <div className="ms-4">
                        <h3 className="fw-bold report-title m-0 lh-sm text-dark">
                          Attendance Insights Hub
                        </h3>
                        <p className="text-secondary fs-6 fw-medium mb-0 lh-sm">
                          Generate, filter &amp; download every attendance
                          report — all in one place.
                        </p>
                      </div>
                    </div>

                    {/* DATASET SELECTOR */}
                    <div className="mt-4">
                      <div className="row g-1">
                        {Object.entries(DATASETS).map(([key, ds]) => {
                          const isActive = activeDataset === key;
                          const count = datasetMap[key]?.data?.length || 0;
                          return (
                            <div className="col-md-3 col-6" key={key}>
                              <div
                                onClick={() => setActiveDataset(key)}
                                className={`p-3 rounded-4 cursor-pointer position-relative ${
                                  isActive
                                    ? `bg-${ds.color} text-white`
                                    : `bg-${ds.color}-soft`
                                }`}
                              >
                                {isActive && (
                                  <span className="position-absolute top-0 end-0 m-2">
                                    <i className="bi bi-check-circle-fill text-white fs-5"></i>
                                  </span>
                                )}
                                <p
                                  className={`m-0 fw-normal fs-5 lh-sm ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {ds.label}
                                </p>
                                <h3
                                  className={`fw-bold m-0 lh-1 mt-1 ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {count.toLocaleString("en-IN")}
                                </h3>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* FILTERS */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-funnel-fill me-2 text-secondary"></i>
                      Refine Results
                    </p>
                    <span
                      onClick={resetFilters}
                      className="badge bg-danger-soft text-dark rounded-3 px-3 py-2 cursor-pointer fw-medium"
                    >
                      <i className="bi bi-arrow-counterclockwise me-1"></i>{" "}
                      Reset
                    </span>
                  </div>

                  {/* Search */}
                  <div className="bg-light rounded-4 mb-3">
                    <form
                      className="d-flex align-items-center"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <span className="position-absolute ps-3 search-icon">
                        <i className="bi bi-filter-circle-fill ms-1" />
                      </span>
                      <input
                        type="search"
                        className="form-control ps-7 rounded-4 border-0 bg-light"
                        placeholder="Search name, admission no, bus no..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </form>
                  </div>

                  {/* Class & Section */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Class &amp; Section
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-mortarboard-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1 h-100"
                            isClearable
                            placeholder="All Classes"
                            value={
                              selectedClass
                                ? { value: selectedClass, label: selectedClass }
                                : null
                            }
                            onChange={(opt) => {
                              setSelectedClass(opt ? opt.value : "");
                              setSelectedSection("");
                            }}
                            options={classOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-bookmark-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1 h-100"
                            isClearable
                            isDisabled={!selectedClass}
                            placeholder={
                              selectedClass
                                ? "All Sections"
                                : "Select class first"
                            }
                            value={
                              selectedSection
                                ? {
                                    value: selectedSection,
                                    label: selectedSection,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedSection(opt ? opt.value : "")
                            }
                            options={sectionOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Date range */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Date Range
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-event text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          From
                        </p>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-check text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          To
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* LIVE SNAPSHOT */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-pie-chart-fill me-2 text-secondary"></i>
                      Live Snapshot
                    </p>
                    <span className="badge bg-success-soft text-dark rounded-3 px-4 py-2 fw-medium">
                      <i className="bi bi-broadcast me-1"></i> Real-time
                    </span>
                  </div>

                  <div className="row g-2">
                    {/* Donut */}
                    <div className="col-md-5">
                      <div className="bg-info-soft rounded-4 p-4 h-100 d-flex flex-column align-items-center justify-content-center position-relative">
                        <svg viewBox="0 0 100 100" className="w-75">
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="12"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#198754"
                            strokeWidth="12"
                            strokeDasharray={`${summary.total ? (summary.present / summary.total) * 251.2 : 0} 251.2`}
                            transform="rotate(-90 50 50)"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#dc3545"
                            strokeWidth="12"
                            strokeDasharray={`${summary.total ? (summary.absent / summary.total) * 251.2 : 0} 251.2`}
                            strokeDashoffset={`${summary.total ? -(summary.present / summary.total) * 251.2 : 0}`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="position-absolute text-center">
                          <p className="m-0 fs-6 text-secondary lh-1">Total</p>
                          <h2 className="m-0 fw-bold text-dark lh-1 mt-1">
                            {summary.total.toLocaleString("en-IN")}
                          </h2>
                        </div>
                      </div>
                    </div>

                    {/* Stat grid */}
                    <div className="col-md-7">
                      <div className="row g-1 h-100">
                        <div className="col-6">
                          <div className="bg-success-soft rounded-4 p-4 h-100">
                            <i className="bi bi-person-check-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Present
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {summary.present.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {summary.total
                                ? Math.round(
                                    (summary.present / summary.total) * 100,
                                  )
                                : 0}
                              % of total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-danger-soft rounded-4 p-4 h-100">
                            <i className="bi bi-person-x-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Absent
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {summary.absent.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {summary.total
                                ? Math.round(
                                    (summary.absent / summary.total) * 100,
                                  )
                                : 0}
                              % of total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-warning-soft rounded-4 p-4 h-100">
                            <i className="bi bi-bus-front-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Bus Pickup ✓
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {summary.busPickupPresent.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              Present today
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-dark-soft rounded-4 p-4 h-100">
                            <i className="bi bi-clipboard-check text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Leave Pending
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {summary.pendingLeave.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {summary.approvedLeave} approved
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── RIGHT COLUMN ── */}
              <div className="col-lg-4">
                <div className="mb-3">
                  <div className="mt-2 ms-4">
                    <nav aria-label="breadcrumb">
                      <ol className="breadcrumb pe-3">
                        <li className="breadcrumb-item">
                          <a href="#">Reports &amp; Analytics</a>
                        </li>
                        <li
                          className="breadcrumb-item active"
                          aria-current="page"
                        >
                          Attendance
                        </li>
                      </ol>
                    </nav>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-4">
                  {/* Avatar + count */}
                  <div className="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                    <div className="avatar-group">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <span className="avatar avatar-md" key={num}>
                          <img
                            alt={`avatar-${num}`}
                            src={`/assets/images/students/${num}.jpg`}
                            className="rounded-circle"
                          />
                        </span>
                      ))}
                    </div>
                    <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                      {filteredData.length.toLocaleString("en-IN")}
                    </h3>
                  </div>

                  {/* Title row */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      {currentLoading ? (
                        <div
                          className="spinner-border text-dark data-loader-spinner"
                          role="status"
                        ></div>
                      ) : (
                        <img src="/assets/images/folder.svg" alt="" />
                      )}
                      <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                        {DATASETS[activeDataset]?.label}
                        <br />
                        <span className="text-secondary fs-6 fw-medium lh-sm">
                          {reports.length} Reports Available
                        </span>
                      </p>
                    </div>
                    <span
                      onClick={
                        activeDataset === "school"
                          ? reportSchoolAll
                          : activeDataset === "bus"
                            ? reportBusAll
                            : activeDataset === "leave"
                              ? reportLeaveAll
                              : reportSchoolAll
                      }
                      className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                    >
                      Export
                    </span>
                  </div>

                  {/* Category tabs */}
                  <div className="mt-3 d-flex flex-wrap gap-1">
                    {REPORT_CATEGORIES.map((cat) => (
                      <div
                        key={cat.key}
                        className={`bg-dark-soft rounded-3 cursor-pointer px-3 py-1 fs-6 ${
                          activeCategory === cat.key
                            ? "bg-warning text-white border-warning"
                            : ""
                        }`}
                        onClick={() => setActiveCategory(cat.key)}
                      >
                        {cat.label}
                      </div>
                    ))}
                  </div>

                  {/* Report buttons */}
                  <div className="px-4 py-2 mt-3 bg-light rounded-4">
                    <li className="list-group-item px-0">
                      <a
                        className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                        data-bs-toggle="collapse"
                        href="#attendanceReportsCollapse"
                        role="button"
                        aria-expanded="true"
                        aria-controls="attendanceReportsCollapse"
                      >
                        <p className="m-0 fw-medium lh-sm text-dark">
                          Available Reports ({visibleReports.length})
                        </p>
                        <span className="chevron-arrow ms-4">
                          <i className="bi bi-chevron-down fs-4" />
                        </span>
                      </a>
                      <div className="collapse" id="attendanceReportsCollapse">
                        <div className="py-3">
                          <div className="rounded-4 d-flex flex-wrap">
                            {visibleReports.map((r, i) => (
                              <span
                                key={i}
                                onClick={r.action}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                {r.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  </div>

                  {/* Quick action cards */}
                  <div className="mt-2">
                    <div className="row g-1">
                      <div className="col-6">
                        <div
                          onClick={reportAttendanceSummary}
                          className="p-3 bg-success-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Summary
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Class Wise Stats
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportBusSummary}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Bus Summary
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Pickup / Drop
                          </p>
                        </div>
                      </div>
                      <div className="col-12">
                        <div
                          onClick={reportMasterCombined}
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Master Combined Report
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            School + Bus Attendance in One Sheet
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default AttendanceReports;
